import _ from "underscore";
import rison from "rison";
import DriveView from "../../../../DriveView";
import SelectDialog from "../../../framework/selectDialog";
import DateTimeSelect from "../../../framework/dateTimeSelect";
import VehicleSelectDialog from "../../../framework/vehicleSelectDialog";
import { BaseCache, makeDeviceCache } from "../../../../utils/storage/cache";
import { dstCalculator, getStartOfDayUserTimezone, makeHistoricalDayKey } from "../../../../utils/date";
import idSerializer from "geotab-uuid";
import Sync from "../../../../sync/sync";
import Api from "../../../../services/api";
import HosProcess from "../../../../services/hosProcess";
import Validator from "../../../../utils/validator";
import { getServerDate } from "../../../../utils/storage/date";
import dayjs from "../../../../utils/dayjs";
import { CancelablePromise as Promise } from "../../../../utils/cancelablePromise";
import { handleApi } from "../../../../utils/error";
import DutyStatusLog from "../../../../model/DutyStatusLog";
import AutoCompletionInput from "../../../framework/autoCompletionInput";
import { getCurrentVehicle, isSleeperBerthDisabled } from "../../../../utils/device";
import { PageLevel } from "../../../../store/mobile/types";
import { MobileActions } from "../../../../store/mobile/actions";
import { HistoricalDayLoader } from "../../../../services/historicalDayLoader";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import addLogHTML from "./addLog.tmpl";
import { populateViolationReason, roundBasedOnStatus, type IHosViolationSlim, type THosStatus } from "hos-tools";
import { convertMinutesToTimeString, isValidOdometer } from "../../../../utils/helpers";
import DurationSelect from "../../../framework/durationSelect";
import logDurationCalculator from "../../../../utils/logDurationCalculator";
import { SyncType } from "../../../../sync/sync.types";
import PageError from "../../../../utils/pageError";
import { Device } from "../../../../model/Device";
import toastr from "../../../../utils/toastr";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";

export default class AddLogView extends DriveView<Backbone.Model> {
    protected strings = [
        "Saving in progress",
        "You have been {0} since {1}.",
        "Driving",
        "On Duty",
        "in Yard Move",
        "Add",
        "Log added"
    ];
    private readonly template = _.template(addLogHTML);
    private readonly sync: Sync;
    private readonly api: Api;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly validator: Validator;
    private readonly hosUtils: HosProcess;
    private readonly historicalDayLoader: HistoricalDayLoader;
    private readonly workflow: boolean;
    private readonly isEwd: boolean;
    private status: THosStatus;
    private dateTime: Date = getServerDate();
    private duration?: number | null;
    private device?: INamedEntity | "NoDeviceId";
    private errors: PageError[];
    private annotationInput?: AutoCompletionInput;
    private logCollection: DutyStatusLog[] = [];
    private previousLog?: DutyStatusLog | null;
    private nextLog?: DutyStatusLog | null;
    private dateTimeErrorLog: [DutyStatusLog, string] | null = null;
    private odometerInput?: HTMLInputElement;

    constructor (options) {
        super(options, {
            title: "Add log",
            activeSection: "hos",
            pageLevel: options.pageLevel || PageLevel.TWO
        });

        this.validator = new Validator(this.app.dutyStatusLogService);
        this.sync = this.app.sync;
        this.isEwd = this.app.store.getState().data.ELDJurisdiction === "EWD";
        this.status = this.isEwd ? "Rest" : "OFF";
        this.errors = [];
        this.api = this.app.api;
        this.deviceCache = makeDeviceCache(this.app.store);
        this.hosUtils = this.app.hosUtils;
        this.workflow = options.workflow || false;
        this.updateLogCollection();
        this.historicalDayLoader = new HistoricalDayLoader(this.api, this.app.dutyStatusLogService, this.app.store);

        if (this.workflow) {
            this.previousLog = this.app.dutyStatusLogService.getLatestStatusLog();
            if (this.previousLog) {
                this.dateTime = this.previousLog.getDate();
                this.dateTime.setMinutes(this.dateTime.getMinutes() + 1);
            }

            this.listenTo(dispatcher, DispatcherEvents.SyncHos, () => {
                this.previousLog = this.app.dutyStatusLogService.getLatestStatusLog();
                this.render();
            });
        } else if (options.state && options.state.dateTime) {
            let passedDate = dstCalculator.toLocalDateTime(options.state.dateTime);
            let now = dstCalculator.toLocalDateTime(getServerDate());
            passedDate.setHours(now.getHours());
            passedDate.setMinutes(now.getMinutes());
            passedDate.setSeconds(this.isEwd ? 0 : now.getSeconds());
            this.dateTime = dstCalculator.toReverseOffsetDateTime(passedDate);

            if (this.isEwd) {
                roundBasedOnStatus(this.dateTime);
                this.previousLog = this.app.dutyStatusLogService.getLogBasedOnDate(this.dateTime, true);
                this.nextLog = this.app.dutyStatusLogService.getLogBasedOnDate(this.dateTime, false);
            }
        }
    }

    get annotation (): string {
        return this.annotationInput?.val || "";
    }

    get odometer (): string {
        return this.odometerInput?.value.trim() || "";
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #add": this.add,
            "click #selectStatus": this.selectStatus,
            "click #selectDate": this.selectDate,
            "click #selectVehicle": this.selectVehicle,
            "click #view-existing-log": this.viewExistingLog,
            "click #editDuration": this.editDuration
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render () {
        let getCurrentDevice = (): Promise<INamedEntity | "NoDeviceId" | undefined> => {
            if (!this.device) {
                let currentVehicle = this.workflow && this.previousLog ? this.previousLog.device : getCurrentVehicle();
                return this.deviceCache.getOrFetch(this.api, currentVehicle).then((vehicles: INamedEntity[] | INamedEntity[][]) => {
                    return vehicles && vehicles[0] && vehicles[0][0];
                });
            }
            return Promise.resolve(this.device);
        };

        this.app.localizerService.initUserUnits(this.translator)
            .then(() => getCurrentDevice())
            .then(vehicle => {
                this.device = vehicle;
                let duration = this.duration || this.getDuration();

                return this.display(this.template({
                    workflow: this.workflow,
                    status: this.status,
                    workflowLastLogString: this.getWorkflowLastLogString(),
                    dateTime: this.dateTime ? dayjs(dstCalculator.toLocalDateTime(this.dateTime)).format("LLL") : null,
                    deviceName: this.device && (this.device as INamedEntity).name,
                    annotation: this.annotation,
                    errors: this.errors.map(error => error.text),
                    showDuration: this.isEwd && duration > 0,
                    duration: convertMinutesToTimeString(duration),
                    dateTimeErrorLog: this.dateTimeErrorLog && this.dateTimeErrorLog[0],
                    existingLogErrorMessage: this.dateTimeErrorLog && this.dateTimeErrorLog[1],
                    odometer: this.odometer,
                    isEwd: this.isEwd,
                    units: this.app.localizerService.getDistanceUnit(true)
                }));
            })
            .then(() => {
                if (this.isEwd) {
                    let odometerInputContainer = <HTMLInputElement> this.el.querySelector("#addOdometerInput");
                    if (odometerInputContainer) {
                        this.odometerInput = odometerInputContainer;
                    }
                }

                let annotationInputContainer = <HTMLInputElement> this.el.querySelector("#addAnnotationForm");
                if (annotationInputContainer) {
                    this.annotationInput = new AutoCompletionInput({
                        app: this.app,
                        el: annotationInputContainer,
                        action: this.status,
                        value: this.annotation,
                        placeholder: "Add an annotation...",
                        ariaLabel: "Add an annotation to this log."
                    });
                    return this.annotationInput.render();
                }
                return Promise.resolve();
            })
            .catch(handleApi);

        return this;
    }

    viewExistingLog() {
        if (!this.dateTimeErrorLog) {
            return;
        }

        let ruleResult = this.hosUtils.getRuleResult();
        let violations = ruleResult ? ruleResult.violations : [];
        let logId = this.dateTimeErrorLog[0].id;
        let filteredViolations: IHosViolationSlim[] = violations
            .filter(vio => vio.logId === logId)
            .map((vio: any) => ({ start: new Date(vio.fromDate).toISOString(), reason: populateViolationReason(vio) }));

        this.dateTimeErrorLog && this.app.router.navigate(`hos/tabs/logs/view,${ rison.encode({ id: logId, violations: filteredViolations }) }`, true);
    }

    selectStatus() {
        let statusDialog = new SelectDialog({
            translator: this.translator,
            title: "Choose status",
            selectCallback: statusObj => {
                this.status = statusObj.id;
                this.checkForAnnotation();
                this.render();
            },
            isUserNightMode: this.app.store.getState().data.nightMode
        });

        let formatRenderItems = (status: string) => {
            return {
                id: status,
                name: status,
                isSelected: status === this.status
            };
        };

        if (this.isEwd) {
            statusDialog.render(["Rest", "Work"].map(formatRenderItems));
        } else if (isSleeperBerthDisabled(this.app.store, this.device)) {
            statusDialog.render(["OFF", "ON", "D"].map(formatRenderItems));
        } else {
            statusDialog.render(["OFF", "SB", "ON", "D"].map(formatRenderItems));
        }
    }

    selectDate () {
        let dateTimeDialog = new DateTimeSelect({
            translator: this.translator,
            language: (this.app.translationService.getLanguage() || "en") as TSupportedLanguage,
            // We need to show datetime according to user options' timezone
            date: this.dateTime || getServerDate(),
            selectCallback: dateTime => {
                // but set according to device's timezone
                this.dateTime = dstCalculator.toReverseOffsetDateTime(dateTime);
                if (this.isEwd) {
                    !this.workflow && (this.previousLog = this.app.dutyStatusLogService.getLogBasedOnDate(this.dateTime, true));
                    this.nextLog = this.app.dutyStatusLogService.getLogBasedOnDate(this.dateTime, false);
                }
                this.checkForAnnotation();
                this.render();
            },
            isUserNightMode: this.app.store.getState().data.nightMode,
            isEwd: this.isEwd
        });

        dateTimeDialog.render();
    }

    selectVehicle () {
        let vehicleDialog = new VehicleSelectDialog({
            translator: this.translator,
            app: this.app,
            selectedVehicleId: this.device && (this.device as IIdEntity).id,
            selectCallback: deviceNamedEntity => {
                this.device = deviceNamedEntity;
                if (this.status === "SB" && isSleeperBerthDisabled(this.app.store, deviceNamedEntity)) {
                    this.status = "OFF";
                }
                this.checkForAnnotation();
                this.render();
            }
        });

        vehicleDialog.render();
    }

    getAnnotation(driver: IIdEntity, dateString: string) {
        return {
            id: idSerializer.get(),
            driver,
            comment: this.annotation,
            dateTime: dateString
        };
    }

    createNewLog(dateString, driver) {
        let newLog = new DutyStatusLog({
            id: idSerializer.get(),
            status: this.status,
            dateTime: dateString,
            origin: "Manual",
            device: {id: this.device && (this.device as IIdEntity).id ? (this.device as IIdEntity).id : "NoDeviceId" },
            driver
        });

        return newLog;
    }

    checkValidOdometer () {
        if (this.isEwd && (!this.odometer || Number(this.odometer) <= 0 || (typeof this.odometer === "string" && Number.isNaN(Number(this.odometer))))) {
            this.errors = [new PageError("odometer", "You must enter a valid odometer number.")];
            this.render();
            return false;
        }

        return true;
    }

    mergeErrors(errors: PageError[], validatorErrors: PageError[]): PageError[] {
        const errorMap = new Map<string, PageError>();
        for (const error of errors) {
            if (!errorMap.has(error.text)) {
                errorMap.set(error.text, error);
            }
        }
        for (const error of validatorErrors) {
            if (!errorMap.has(error.text)) {
                errorMap.set(error.text, error);
            }
        }
        return Array.from(errorMap.values());
    }

    add () {
        this.errors = [];
        this.updateLogCollection();
        let logCollection = this.logCollection;

        if (this.isEwd && !isValidOdometer(this.odometer)) {
            this.errors = [new PageError("odometer", "You must enter a valid odometer number.")];
            this.render();
            return;
        }

        let currentUser = this.app.userService.getCurrentUser();
        let driver = { id: currentUser ? currentUser.id : "NoUserId" };
        let dateTime = (this.dateTime || getServerDate());
        let dateString = dateTime.toISOString();
        let newLog = this.createNewLog(dateString, driver);

        if (this.isEwd) {
            newLog.odometer = this.app.localizerService.toMetresFromLocalDistance(Number(this.odometer));
        }

        if (this.workflow && this.dateTime && this.previousLog && this.dateTime < this.previousLog.getDate()) {
            this.errors = [new PageError("datetime", "New logs cannot be added before your previous log.")];
            this.render();
            return;
        }

        let logWithSameDateTime = this.app.dutyStatusLogService.getLogWithIdenticalDateTime(this.dateTime);
        if (logWithSameDateTime) {
            this.dateTimeErrorLog = [logWithSameDateTime, "A log already exists for the selected date."];
            this.render();
            return;
        }

        if (this.isEwd && this.previousLog && this.previousLog.status === this.status) {
            this.dateTimeErrorLog = [this.previousLog, "This Status and Date are already selected."];
            this.render();
            return;
        }

        let logsToAdd: DutyStatusLog[] = [];
        let logsToEdit: DutyStatusLog[] = [];
        if (this.duration) {
            let logEndDate = new Date(this.dateTime.getTime());
            logEndDate.setMinutes(logEndDate.getMinutes() + this.duration);

            if (this.app.dutyStatusLogService.isInvalidFutureDate(logEndDate, this.app.dutyStatusLogService.getLogBasedOnDate(logEndDate, true))) {
                this.errors = [new PageError("datetime", "The selected Duration is invalid because it creates a log set in the future.")];
                this.render();
                return;
            }

            let [newLogs, editedLogs] = logDurationCalculator().getLogsToAddEdit(logCollection, newLog, logEndDate, false);
            logsToAdd = newLogs;
            logsToEdit = editedLogs;
        } else {
            logsToAdd.push(newLog);
        }

        let allLogs: DutyStatusLog[] = logsToAdd.concat(logsToEdit);
        allLogs.map(log => log.addAnnotation(this.getAnnotation(driver, dateString)));

        if (allLogs.length > 0 && this.validator.validate(allLogs[0], logCollection, undefined, this.isEwd ? this.app.dutyStatusLogService.getLogBasedOnDate(dateTime, true) : null)) {
            let addBtn = <HTMLInputElement>document.getElementById("add");
            addBtn.innerHTML = this.getString("Saving in progress");
            addBtn.disabled = true;

            this.annotationInput && this.annotationInput.cacheHint(this.annotation);

            const newLogs = this.app.dutyStatusLogService.getAdditionalLogsBeforeNewLogIfDrivingInExemption(dateTime, currentUser as IUser, false);
            newLogs.length && logsToAdd.push(...newLogs);

            Promise.all(logsToAdd.map(log => this.sync.dutyStatusLogs.addItem(log, true)))
                .then(() => Promise.all(logsToEdit.map(log => this.sync.dutyStatusLogs.setItem(log, true))))
                .then(() => this.app.sync.synchronize(undefined, undefined, [SyncType.HOS]))
                .then(() => {
                    toastr.success(this.getString("Log added"));
                    if (this.workflow) {
                        dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, newLog.id);
                    } else {
                        let dayOfLog = getStartOfDayUserTimezone(dateTime);
                        // A historical day (outside the sync window) is served from the cached
                        // historicalDayTracker entry. Adding a log changes that day and the status
                        // "continued from previous day" carried into every later day, so invalidate the
                        // added log's day and all cached days after it — they re-fetch from the server
                        // (now including this log) instead of rendering a stale cached copy.
                        this.historicalDayLoader.invalidateDayAndFollowing(driver.id, makeHistoricalDayKey(dayOfLog));
                        this.app.store.dispatch(MobileActions.setLastViewedLog({ date: dayOfLog.toISOString() }));
                        this.app.router.navigate("hos/tabs/logs/logs", true);
                    }
                })
                .catch(e => {
                    addBtn.innerHTML = this.getString("Add");
                    addBtn.disabled = false;
                    e && handleApi(e);
                });
        } else {
            const mergedErrors = this.mergeErrors(this.errors, this.validator.errors);
            this.errors = mergedErrors;
            this.render();
        }
    }

    getWorkflowLastLogString(): string {
        if (!this.previousLog) {
            return "";
        }
        let statusString: string;
        let latestStatus = this.previousLog.status;
        switch (latestStatus) {
            case "D":
                statusString = this.getString("Driving");
                break;
            case "ON":
                statusString = this.getString("On Duty");
                break;
            case "YM":
                statusString = this.getString("in Yard Move");
                break;
            default:
                statusString = "";
                break;
        }

        let dateTime = dayjs(dstCalculator.toLocalDateTime(this.previousLog.getDate())).format("LLL");
        return this.getString("You have been {0} since {1}.").replace("{0}", statusString).replace("{1}", dateTime);
    }

    remove() {
        this.annotationInput && this.annotationInput.remove();
        if (this.isEwd) {
            this.odometerInput && this.odometerInput.remove();
        }
        super.remove();
        return this;
    }

    private updateLogCollection() {
        this.logCollection = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs().filter(hosLog => hosLog.isNormalLog()));
    }

    private getDuration() {
        let logEndDate = this.nextLog ? this.nextLog.getDate() : getServerDate();
        return (logEndDate.getTime() - this.dateTime.getTime()) / (1000 * 60);
    }

    private editDuration() {
        let startingDuration = this.duration || this.getDuration();
        let durationDialog = new DurationSelect({
            translator: this.translator,
            duration: startingDuration,
            resetCallback: () => {
                this.duration = null;
                this.render();
            },
            selectCallback: (duration) => {
                if (duration === startingDuration) {
                    return;
                }

                if (duration === 0) {
                    this.errors.push(new PageError("duration",  "The Duration cannot be 0."));
                    this.render();
                    return;
                }
                this.duration = duration;
                this.render();
            },
            isUserNightMode: this.app.store.getState().data.nightMode,
            isEwd: this.isEwd
        });

        durationDialog.render();
    }

    private checkForAnnotation() {
        if (!this.annotation) {
            const annotationErrorIndex = this.errors.findIndex(error => error.text === "Add an annotation to this log.");
            if (annotationErrorIndex === -1) {
                this.errors.push(new PageError("annotation", "Add an annotation to this log."));
            }
        } else {
            this.errors = this.errors.filter(error => error.text !== "Add an annotation to this log.");
        }
    }
}
